import { DraftStepsEditor, type DraftStep } from '@/components/processes/DraftStepsEditor';
import { ProcessStepsList, type ProcessStep } from '@/components/processes/ProcessStepsList';
import type {
  Process,
  ProcessFailureFrequency,
  ProcessRunFrequency,
  ProcessWithSteps,
} from '@/config/objects/processes';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormFieldGroup,
  FormPageLayout,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  SwitchField,
  Textarea,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface CurrentUser {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
}

interface FormData {
  title: string;
  description?: string;
  whyItMatters?: string;
  successCriteria?: string;
  failureModes?: string;
  failureFrequency?: ProcessFailureFrequency | null;
  runFrequency?: ProcessRunFrequency | null;
  estimatedMinutes?: number | null;
  ownerId?: string | null;
}

const RUN_FREQUENCY_OPTIONS: { label: string; value: ProcessRunFrequency }[] = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Quarterly', value: 'quarterly' },
  { label: 'Ad-hoc', value: 'ad_hoc' },
];

const FAILURE_FREQUENCY_OPTIONS: { label: string; value: ProcessFailureFrequency }[] = [
  { label: 'Rarely', value: 'rarely' },
  { label: 'Sometimes', value: 'sometimes' },
  { label: 'Often', value: 'often' },
  { label: 'Always', value: 'always' },
];

/** Process create/edit form page. */
export function ProcessFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);

  const [formData, setFormData] = useState<FormData>({
    title: '',
  });
  const [draftSteps, setDraftSteps] = useState<DraftStep[]>([]);

  // In edit mode, fetch live steps so the form can render the same editor
  // that lives on the detail page (each change persists immediately).
  const { data: existingProcessData } = useQuery<{ data: ProcessWithSteps }>({
    queryKey: ['Process', id],
    queryFn: () => apiClient.get(`/processes/${id}`),
    enabled: !!id,
  });
  const existingSteps: ProcessStep[] =
    existingProcessData?.data?.steps?.map((s) => ({
      id: s.id,
      body: s.body,
      sortOrder: s.sortOrder,
      createdAt: s.createdAt,
      updatedAt: s.updatedAt,
    })) ?? [];

  // Fetch current user once on mount so we can default ownership to "me".
  useEffect(() => {
    apiClient
      .get<{ data: CurrentUser }>('/users/me')
      .then((response) => setCurrentUser(response.data))
      .catch(() => {
        // Non-fatal — owner toggle just won't appear.
      });
  }, [apiClient]);

  // For new processes, default owner to current user once it's loaded.
  useEffect(() => {
    if (id || !currentUser) return;
    setFormData((prev) =>
      prev.ownerId === undefined ? { ...prev, ownerId: currentUser.id } : prev,
    );
  }, [id, currentUser]);

  // Load existing process when editing.
  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    apiClient
      .get<{ data: Process & { updatedAt?: string } }>(`/processes/${id}`)
      .then((response) => {
        const data = response.data;
        setFormData({
          title: data.title || '',
          description: data.description ?? undefined,
          whyItMatters: data.whyItMatters ?? undefined,
          successCriteria: data.successCriteria ?? undefined,
          failureModes: data.failureModes ?? undefined,
          failureFrequency: data.failureFrequency ?? null,
          runFrequency: data.runFrequency ?? null,
          estimatedMinutes: data.estimatedMinutes ?? null,
          ownerId: data.ownerId ?? null,
        });
        if (data.updatedAt) setLastSaved(new Date(data.updatedAt));
      })
      .catch((error) => {
        toast.error(error?.message || 'Failed to load process');
      })
      .finally(() => setIsLoading(false));
  }, [id, apiClient]);

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setIsSaving(true);
    try {
      const endpoint = id ? `/processes/${id}` : '/processes';
      const method = id ? 'put' : 'post';
      const result = await apiClient[method]<{ data: { id: string } }>(endpoint, formData);

      // For new processes, persist any draft steps the user added in the form.
      // Done sequentially so sortOrder reflects the on-screen order.
      if (!id && draftSteps.length > 0) {
        const newId = result.data.id;
        for (let i = 0; i < draftSteps.length; i++) {
          await apiClient.post(`/processes/${newId}/steps`, {
            body: draftSteps[i].body,
            sortOrder: (i + 1) * 10,
          });
        }
      }

      setLastSaved(new Date());
      toast.success(id ? 'Process updated successfully' : 'Process created successfully');
      if (id) {
        navigate(-1);
      } else {
        navigate(`/processes/${result.data.id}`);
      }
    } catch (error) {
      toast.error((error as Error)?.message || 'Failed to save process');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(id ? `/processes/${id}` : '/processes');
  };

  const isOwnedByMe = !!currentUser && formData.ownerId === currentUser.id;
  const isOwnedByOther =
    !!formData.ownerId && !!currentUser && formData.ownerId !== currentUser.id;

  if (isLoading) {
    return (
      <FormPageLayout>
        <FormPageLayout.Header>
          <div className="flex items-center gap-4">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-60" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-32" />
          </div>
        </FormPageLayout.Header>
        <FormPageLayout.Content>
          {[1, 2, 3].map((i) => (
            <Card key={i} className="rounded-xl border bg-card shadow-sm">
              <CardHeader className="px-6 py-4 border-b">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-64" />
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-2/3" />
              </CardContent>
            </Card>
          ))}
        </FormPageLayout.Content>
      </FormPageLayout>
    );
  }

  return (
    <FormPageLayout>
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center h-9 w-9 rounded-lg border bg-card hover:bg-muted transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">
              {id ? 'Edit Process' : 'Document a Process'}
            </h1>
            {id && formData.title && (
              <p className="text-sm text-muted-foreground">{formData.title}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} loading={isSaving} icon={Save}>
            {id ? 'Save Changes' : 'Create Process'}
          </Button>
        </div>
      </FormPageLayout.Header>

      <FormPageLayout.Content>
        {/* ── Overview ────────────────────────────────────────────────── */}
        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">Overview</CardTitle>
            <CardDescription>What this process is and why it matters</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <FormFieldGroup
              name="title"
              label="Title"
              required
              description="A short, recognizable name for this process — what you'd call it in conversation."
            >
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Name this process"
              />
            </FormFieldGroup>

            <FormFieldGroup
              name="description"
              label="What is the process?"
              description="A one- or two-sentence summary of the work, in plain language."
            >
              <Textarea
                id="description"
                value={formData.description ?? ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="In one or two sentences, what is this process?"
                rows={3}
              />
            </FormFieldGroup>

            <FormFieldGroup
              name="whyItMatters"
              label="Why this matters"
              description="The business reason this process exists — what would break or be missed if no one did it."
            >
              <Textarea
                id="whyItMatters"
                value={formData.whyItMatters ?? ''}
                onChange={(e) => setFormData({ ...formData, whyItMatters: e.target.value })}
                placeholder="Why is this process important? What outcome does it produce?"
                rows={3}
              />
            </FormFieldGroup>
          </CardContent>
        </Card>

        {/* ── Outcomes ────────────────────────────────────────────────── */}
        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">Outcomes</CardTitle>
            <CardDescription>What success and failure look like</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <FormFieldGroup
              name="successCriteria"
              label="What success looks like"
              description="How you can tell the process worked — the visible outcome, signal, or confirmation."
            >
              <Textarea
                id="successCriteria"
                value={formData.successCriteria ?? ''}
                onChange={(e) => setFormData({ ...formData, successCriteria: e.target.value })}
                placeholder="How do you know the process worked?"
                rows={3}
              />
            </FormFieldGroup>

            <FormFieldGroup
              name="failureModes"
              label="What failure looks like"
              description="Common ways this goes wrong — error messages, missing data, escalations, anything that tells you it failed."
            >
              <Textarea
                id="failureModes"
                value={formData.failureModes ?? ''}
                onChange={(e) => setFormData({ ...formData, failureModes: e.target.value })}
                placeholder="What does it look like when this process goes wrong?"
                rows={3}
              />
            </FormFieldGroup>

            <FormFieldGroup
              name="failureFrequency"
              label="How often does this fail?"
              description="Roughly how often the process produces a bad result and needs intervention."
            >
              <Select
                value={formData.failureFrequency ?? 'unset'}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    failureFrequency: value === 'unset' ? null : (value as ProcessFailureFrequency),
                  })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">Not set</SelectItem>
                  {FAILURE_FREQUENCY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormFieldGroup>
          </CardContent>
        </Card>

        {/* ── Steps ───────────────────────────────────────────────────── */}
        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">Steps to reproduce</CardTitle>
            <CardDescription>
              {id
                ? 'Steps in this process. Each change is saved as you make it.'
                : 'List the steps below — they will be saved when you create the process.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {id ? (
              <ProcessStepsList processId={id} steps={existingSteps} />
            ) : (
              <DraftStepsEditor value={draftSteps} onChange={setDraftSteps} />
            )}
          </CardContent>
        </Card>

        {/* ── Cadence ─────────────────────────────────────────────────── */}
        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">Cadence</CardTitle>
            <CardDescription>Owner, frequency, and estimated effort</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <FormFieldGroup
                name="runFrequency"
                label="How often is this run?"
                description="Roughly how often someone actually does this — daily, weekly, etc. Helps prioritize what to automate first."
              >
                <Select
                  value={formData.runFrequency ?? 'unset'}
                  onValueChange={(value) =>
                    setFormData({
                      ...formData,
                      runFrequency: value === 'unset' ? null : (value as ProcessRunFrequency),
                    })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unset">Not set</SelectItem>
                    {RUN_FREQUENCY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormFieldGroup>

              <FormFieldGroup
                name="estimatedMinutes"
                label="Estimated minutes per run"
                description="Your best guess of how long one run takes, end to end. Combined with frequency, this is the time-saved estimate."
              >
                <Input
                  id="estimatedMinutes"
                  type="number"
                  min={0}
                  value={formData.estimatedMinutes ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      estimatedMinutes:
                        e.target.value === '' ? null : Math.max(0, parseInt(e.target.value)),
                    })
                  }
                  placeholder="e.g. 30"
                />
              </FormFieldGroup>
            </div>

            {currentUser && !isOwnedByOther && (
              <SwitchField
                label="I own this process"
                description="When on, this process is assigned to you. Turn off to leave it unassigned."
                checked={isOwnedByMe}
                onCheckedChange={(checked) =>
                  setFormData({
                    ...formData,
                    ownerId: checked ? currentUser.id : null,
                  })
                }
              />
            )}

            {isOwnedByOther && (
              <p className="text-sm text-muted-foreground">
                This process is owned by another user. Owner reassignment is not yet available
                in the form.
              </p>
            )}
          </CardContent>
        </Card>
      </FormPageLayout.Content>

      <FormPageLayout.Footer lastSaved={lastSaved}>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} loading={isSaving} icon={Save}>
            {id ? 'Save Changes' : 'Create Process'}
          </Button>
        </div>
      </FormPageLayout.Footer>
    </FormPageLayout>
  );
}
